import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin, Clock, CheckCircle2, ChevronDown, Check } from "lucide-react";
import { BUSINESS } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "EV Charger Installation",
  "Electrical Panel Upgrade",
  "General Electrical Work",
  "Commercial Wiring",
  "Lighting Design & Install",
  "Security & Home Automation",
  "Emergency Service",
  "Other",
];

const CITIES = [
  "Vancouver",
  "Burnaby",
  "Richmond",
  "Surrey",
  "White Rock",
  "Langley",
  "Coquitlam",
  "Delta",
  "North Vancouver",
  "West Vancouver",
  "Maple Ridge",
  "Abbotsford",
  "New Westminster",
  "Other / Lower Mainland",
];

export default function ContactSection() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    contact: "",
    city: "",
    services: [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleService = (svc) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(svc)
        ? f.services.filter((s) => s !== svc)
        : [...f.services, svc],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.contact.trim()) {
      toast.error("Please fill in your name and contact info.");
      return;
    }
    if (!form.services.length) {
      toast.error("Pick at least one service you're interested in.");
      return;
    }
    if (!form.city) {
      toast.error("Please pick your city.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      toast.success("Request sent — we'll be in touch within 1 business hour.");
      setForm({
        first_name: "",
        last_name: "",
        contact: "",
        city: "",
        services: [],
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-white text-[#0A0A0A] border-t border-[#E4E4E7]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">
              — Get In Touch
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-[0.95]">
              Let&rsquo;s talk about
              <br />
              <span className="text-[#E60000]">your project.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/65 leading-relaxed max-w-md">
              Free, no-obligation estimates. Fill out the form and we&rsquo;ll get
              back to you within one business hour — or call us direct.
            </p>

            <div className="mt-10 space-y-4">
              <ContactRow icon={Phone} label="Phone" value={BUSINESS.phone} href={BUSINESS.phoneHref} />
              <ContactRow icon={Mail} label="Email" value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} />
              <ContactRow icon={MapPin} label="Address" value={BUSINESS.address} />
              <ContactRow icon={Clock} label="Hours" value={BUSINESS.hours} />
            </div>

            <div className="mt-8 pt-6 border-t border-[#E4E4E7]">
              <div className="text-[10px] tracking-[0.24em] uppercase text-black/45">
                Certifications
              </div>
              <div className="mt-2 text-sm text-black/75">
                TSBC Licensed · CleanBC Approved · $5M Liability Insured · WorkSafeBC Registered
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="border border-[#E4E4E7] p-6 md:p-10 bg-[#F4F4F5]"
            >
              {submitted ? (
                <div data-testid="quote-success" className="py-16 text-center">
                  <CheckCircle2 className="w-12 h-12 text-[#E60000] mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-display text-3xl font-bold text-black">Request received.</h3>
                  <p className="mt-3 text-black/65 max-w-md mx-auto">
                    Thanks — a journeyman electrician will reach out within one business hour. For urgent issues, call{" "}
                    <a className="text-[#E60000] underline" href={BUSINESS.phoneHref}>
                      {BUSINESS.phone}
                    </a>.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-xs tracking-[0.2em] uppercase text-black/55 hover:text-black"
                  >
                    Send another request →
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="First Name"
                      value={form.first_name}
                      onChange={update("first_name")}
                      testId="input-first-name"
                      required
                    />
                    <Field
                      label="Last Name"
                      value={form.last_name}
                      onChange={update("last_name")}
                      testId="input-last-name"
                      required
                    />
                  </div>
                  <div className="mt-5 grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Phone or Email"
                      value={form.contact}
                      onChange={update("contact")}
                      testId="input-contact"
                      placeholder="e.g. 604-000-0000 or you@email.com"
                      required
                    />
                    <div>
                      <label className="block text-[10px] tracking-[0.24em] uppercase text-black/55 mb-2">
                        City
                      </label>
                      <div className="relative">
                        <select
                          data-testid="input-city"
                          value={form.city}
                          onChange={update("city")}
                          className="appearance-none w-full bg-white border border-[#E4E4E7] focus:border-[#E60000] focus:outline-none px-4 py-3 pr-10 text-black text-sm rounded-none"
                        >
                          <option value="" disabled>
                            Select your city…
                          </option>
                          {CITIES.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          className="w-4 h-4 text-black/40 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                          strokeWidth={2.5}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className="block text-[10px] tracking-[0.24em] uppercase text-black/55 mb-2">
                      Service(s) needed
                      <span className="ml-2 lowercase tracking-normal text-black/40">
                        (pick one or more)
                      </span>
                    </label>
                    <div
                      role="group"
                      aria-label="Services needed"
                      data-testid="input-services"
                      className="flex flex-wrap gap-2"
                    >
                      {SERVICES.map((s) => {
                        const active = form.services.includes(s);
                        return (
                          <button
                            key={s}
                            type="button"
                            role="checkbox"
                            aria-checked={active}
                            onClick={() => toggleService(s)}
                            data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`inline-flex items-center gap-1.5 border text-sm font-semibold px-3.5 py-2 transition-colors ${
                              active
                                ? "bg-[#E60000] border-[#E60000] text-white"
                                : "bg-white border-[#E4E4E7] text-black/75 hover:border-black"
                            }`}
                          >
                            {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                            {s}
                          </button>
                        );
                      })}
                    </div>
                    {form.services.length > 0 && (
                      <div className="mt-2 text-[11px] text-black/50">
                        Selected: <span className="text-black/80 font-semibold">{form.services.join(", ")}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-5">
                    <label className="block text-[10px] tracking-[0.24em] uppercase text-black/55 mb-2">
                      Tell us about your project
                    </label>
                    <textarea
                      data-testid="input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={4}
                      placeholder="A quick line about what you need — panel upgrade, EV charger, lighting, etc."
                      className="w-full bg-white border border-[#E4E4E7] focus:border-[#E60000] focus:outline-none px-4 py-3 text-black text-sm rounded-none resize-none placeholder:text-black/30"
                    />
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="text-xs text-black/55 max-w-xs">
                      By submitting, you agree to be contacted about your quote and accept our{" "}
                      <Link
                        to="/privacy-policy"
                        data-testid="form-privacy-link"
                        className="underline underline-offset-2 hover:text-[#E60000] transition-colors"
                      >
                        Privacy Policy
                      </Link>
                      . We never share your info.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="submit-quote"
                      className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] disabled:opacity-60 text-white font-bold tracking-wide px-8 py-4 text-sm transition-colors"
                    >
                      {loading ? "Sending..." : "Send Request"}
                      <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 border border-[#E4E4E7] flex items-center justify-center shrink-0 group-hover:border-[#E60000] transition-colors">
        <Icon className="w-4 h-4 text-black" strokeWidth={2} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.24em] uppercase text-black/45">
          {label}
        </div>
        <div className="mt-0.5 text-sm md:text-base text-black group-hover:text-[#E60000] transition-colors">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}

function Field({ label, testId, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.24em] uppercase text-black/55 mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        {...rest}
        className="w-full bg-white border border-[#E4E4E7] focus:border-[#E60000] focus:outline-none px-4 py-3 text-black text-sm rounded-none placeholder:text-black/30"
      />
    </div>
  );
}
